(() => {
  'use strict';

  const CONFIG = Object.freeze({
    minViewedProducts: 0,
    minDisplayProducts: 4,
    maxDisplayProducts: 8,
    rootMargin: '900px 0px',
  });

  const query = new URLSearchParams(window.location.search);
  const debug = query.get('sct_debug') === '1';

  let loaded = false;
  let loading = false;

  function readContext() {
    const node =
      document.getElementById(
        'SctRecommendationContext'
      );

    if (!node) {
      return {};
    }

    try {
      return JSON.parse(
        node.textContent || '{}'
      );
    } catch (_error) {
      return {};
    }
  }

  function toNumber(value) {
    const number = Number(value);

    return Number.isFinite(number)
      ? number
      : 0;
  }

  function currentProfile() {
    return (
      window.SctRecommendation
        ?.getProfile?.() ||
      null
    );
  }

  function enoughHistory() {
    return (
      toNumber(
        currentProfile()
          ?.uniqueViewedProducts
      ) >=
      CONFIG.minViewedProducts
    );
  }

  function getSurface() {
    return document.getElementById(
      'SctRecommendationSurface'
    );
  }

  function getTrack(surface) {
    return surface?.querySelector(
      '[data-sct-rec-track]'
    );
  }

  function getAnchorSection() {
    const recommendation =
      document.querySelector(
        'product-recommendations'
      );

    return (
      recommendation
        ?.closest('.shopify-section') ||
      document.querySelector(
        '.related-products'
      )?.closest('.shopify-section') ||
      null
    );
  }

  function placeSurface(surface) {
    const anchor =
      getAnchorSection();

    if (anchor) {
      anchor.insertAdjacentElement(
        'afterend',
        surface
      );

      return anchor;
    }

    const main =
      document.getElementById(
        'MainContent'
      );

    if (main) {
      main.appendChild(surface);
      return main;
    }

    return null;
  }

  function formatPrice(
    priceCents,
    context
  ) {
    const cents =
      toNumber(priceCents);

    if (cents <= 0) {
      return '';
    }

    const currency =
      context.currency ||
      'JPY';

    const locale =
      context.language ||
      document.documentElement.lang ||
      'en';

    const noDecimals =
      currency === 'JPY' ||
      currency === 'KRW';

    try {
      return new Intl.NumberFormat(
        locale,
        {
          style: 'currency',
          currency,
          minimumFractionDigits:
            noDecimals ? 0 : 2,
          maximumFractionDigits:
            noDecimals ? 0 : 2,
        }
      ).format(
        cents / 100
      );
    } catch (_error) {
      return String(
        Math.round(cents / 100)
      );
    }
  }

  function createTextElement(
    tagName,
    className,
    text
  ) {
    const element =
      document.createElement(
        tagName
      );

    element.className =
      className;

    element.textContent =
      text || '';

    return element;
  }

  function createCard(
    product,
    position,
    context
  ) {
    const article =
      document.createElement(
        'article'
      );

    article.className =
      'sct-rec-card';

    article.setAttribute(
      'role',
      'listitem'
    );

    const link =
      document.createElement(
        'a'
      );

    link.className =
      'sct-rec-card__link';

    link.href =
      product.url ||
      `/products/${product.handle}`;

    link.dataset.sctAction =
      'recommendation_product_click';

    link.dataset.sctItemType =
      'product';

    link.dataset.sctItemId =
      String(product.id || '');

    link.dataset.sctItemHandle =
      product.handle || '';

    link.dataset.sctItemName =
      product.title || '';

    link.dataset.sctItemPosition =
      String(position);

    link.dataset.sctProductPosition =
      String(position);

    link.dataset.sctBrand =
      product.vendor || '';

    link.dataset.sctTabName =
      product.source || '';

    link.dataset.sctDestinationType =
      'product';

    link.addEventListener(
      'click',
      () => {
        const result =
          window.SctRecommendation
            ?.track?.(
              'click',
              {
                id: product.id,
                handle: product.handle,
                title: product.title,
                vendor: product.vendor,
                category: product.category,
                price_cents: product.price_cents,
                available: product.available,
                created_at: product.created_at,
                styles: product.styles,
              },
              {
                recommendation_source:
                  'inline',
              }
            );

        if (debug && result) {
          console.log(
            '[SCT Recommendation UI] Click signal:',
            result.reason,
            product.handle
          );
        }
      },
      { passive: true }
    );

    const media =
      document.createElement(
        'div'
      );

    media.className =
      'sct-rec-card__media';

    if (product.image) {
      const image =
        document.createElement(
          'img'
        );

      image.className =
        'sct-rec-card__image';

      image.src =
        product.image;

      image.alt =
        product.title || '';

      image.loading =
        'lazy';

      image.decoding =
        'async';

      media.appendChild(
        image
      );
    } else {
      const placeholder =
        document.createElement(
          'div'
        );

      placeholder.className =
        'sct-rec-card__placeholder';

      media.appendChild(
        placeholder
      );
    }

    const info =
      document.createElement(
        'div'
      );

    info.className =
      'sct-rec-card__info';

    if (product.vendor) {
      info.appendChild(
        createTextElement(
          'p',
          'sct-rec-card__vendor',
          product.vendor
        )
      );
    }

    info.appendChild(
      createTextElement(
        'p',
        'sct-rec-card__title',
        product.title
      )
    );

    const formattedPrice =
      formatPrice(
        product.price_cents,
        context
      );

    if (formattedPrice) {
      info.appendChild(
        createTextElement(
          'p',
          'sct-rec-card__price',
          formattedPrice
        )
      );
    }

    link.appendChild(media);
    link.appendChild(info);
    article.appendChild(link);

    return article;
  }

  function renderProducts(
    surface,
    products
  ) {
    const track =
      getTrack(surface);

    if (!track) {
      return false;
    }

    const context =
      readContext();

    track.textContent = '';

    products
      .slice(
        0,
        CONFIG.maxDisplayProducts
      )
      .forEach(
        (product, index) => {
          track.appendChild(
            createCard(
              product,
              index + 1,
              context
            )
          );
        }
      );

    return true;
  }

  function setupControls(
    surface
  ) {
    const track =
      getTrack(surface);

    if (!track) {
      return;
    }

    const previous =
      surface.querySelector(
        '[data-sct-rec-prev]'
      );

    const next =
      surface.querySelector(
        '[data-sct-rec-next]'
      );

    const scrollAmount =
      () =>
        Math.max(
          240,
          track.clientWidth * 0.82
        );

    previous?.addEventListener(
      'click',
      () => {
        track.scrollBy({
          left:
            -scrollAmount(),
          behavior:
            'smooth',
        });
      }
    );

    next?.addEventListener(
      'click',
      () => {
        track.scrollBy({
          left:
            scrollAmount(),
          behavior:
            'smooth',
        });
      }
    );
  }

  function updateSurfaceForMode(
    surface,
    mode
  ) {
    const heading =
      surface?.querySelector(
        '.sct-rec-surface__heading'
      );

    const language =
      (
        document
          .documentElement
          .lang ||
        ''
      )
        .toLowerCase();

    const isKorean =
      language.startsWith(
        'ko'
      );

    let text =
      isKorean
        ? '추천 아이템'
        : 'Recommended for you';

    let moduleName =
      'Personalized Recommendation';

    if (mode === 'light') {
      text =
        isKorean
          ? '취향에 맞는 아이템'
          : 'Explore your style';

      moduleName =
        'Light Recommendation';
    } else if (
      mode === 'cold'
    ) {
      text =
        isKorean
          ? '이 스타일의 인기 아이템'
          : 'Popular in this style';

      moduleName =
        'Cold Start Recommendation';
    }

    if (heading) {
      heading.textContent =
        text;
    }

    if (surface) {
      surface.dataset
        .sctRecommendationMode =
          mode || '';

      surface.dataset
        .sctModuleName =
          moduleName;
    }
  }

  async function loadSurface(
    surface
  ) {
    if (
      loaded ||
      loading ||
      !enoughHistory()
    ) {
      return;
    }

    const engine =
      window
        .SctRecommendationEngine;

    if (!engine?.recommend) {
      return;
    }

    loading = true;

    try {
      const result =
        await engine.recommend();

      const products =
        Array.isArray(
          result?.products
        )
          ? result.products
          : [];

      if (
        products.length <
        CONFIG.minDisplayProducts
      ) {
        if (debug) {
          console.log(
            '[SCT Recommendation UI] Hidden because fewer than 4 qualified products.',
            products.length
          );
        }

        return;
      }

      updateSurfaceForMode(
        surface,
        result?.mode
      );

      if (
        !renderProducts(
          surface,
          products
        )
      ) {
        return;
      }

      surface.hidden = false;
      loaded = true;

      if (debug) {
        console.log(
          '[SCT Recommendation UI] Displayed.',
          {
            products:
              products.length,
            mode:
              result?.mode,
            activeStyles:
              result?.activeStyles,
          }
        );
      }
    } catch (error) {
      if (debug) {
        console.warn(
          '[SCT Recommendation UI] Failed.',
          error
        );
      }
    } finally {
      loading = false;
    }
  }

  function observeNearRecommendation(
    surface,
    trigger
  ) {
    if (debug) {
      loadSurface(surface);
      return;
    }

    if (
      !('IntersectionObserver' in window) ||
      !trigger
    ) {
      loadSurface(surface);
      return;
    }

    const observer =
      new IntersectionObserver(
        entries => {
          if (
            !entries.some(
              entry =>
                entry.isIntersecting
            )
          ) {
            return;
          }

          observer.disconnect();

          loadSurface(
            surface
          );
        },
        {
          root: null,
          rootMargin:
            CONFIG.rootMargin,
          threshold: 0,
        }
      );

    observer.observe(
      trigger
    );
  }

  function isTreatmentGroup() {
    return (
      window
        .SctRecommendationExperiment
        ?.group ===
      'treatment'
    );
  }

  function init() {
    if (!isTreatmentGroup()) {
      if (debug) {
        console.log(
          '[SCT Recommendation UI] Control group: UI suppressed.'
        );
      }

      return;
    }

    const surface =
      getSurface();

    if (
      !surface ||
      !enoughHistory()
    ) {
      return;
    }

    setupControls(
      surface
    );

    const trigger =
      placeSurface(
        surface
      );

    observeNearRecommendation(
      surface,
      trigger
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();